import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, fmtDate } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader, KpiCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, Building2, ListChecks, Loader2, ArrowRight, Wrench, AlertTriangle, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function TimesheetDateFix() {
  const [companies, setCompanies] = useState([]);
  const [scope, setScope] = useState("all");
  const [mode, setMode] = useState("B");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDates, setEditDates] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  const items = useMemo(() => result?.items || [], [result]);
  const reviewItems = useMemo(() => result?.review_items || [], [result]);
  const selectedIds = useMemo(() => items.filter((i) => selected[i.id]).map((i) => i.id), [items, selected]);
  const allChecked = items.length > 0 && selectedIds.length === items.length;

  const runPreview = async (s = scope, m = mode) => {
    setLoading(true);
    setResult(null);
    setSelected({});
    try {
      const params = { mode: m };
      if (s !== "all") params.company_id = s;
      const { data } = await api.get("/admin/timesheet-date-fix/preview", { params });
      setResult(data);
      const pre = {};
      data.items.forEach((i) => { pre[i.id] = true; });
      setSelected(pre);
      if (data.count === 0 && data.review_count === 0) toast.success("No shifted timesheets found in this scope");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = (v) => {
    const next = {};
    items.forEach((i) => { next[i.id] = !!v; });
    setSelected(next);
  };

  const apply = async () => {
    setApplying(true);
    try {
      const body = { timesheet_ids: selectedIds, mode };
      if (scope !== "all") body.company_id = scope;
      const { data } = await api.post("/admin/timesheet-date-fix/apply", body);
      toast.success(`Corrected ${data.fixed} timesheet${data.fixed === 1 ? "" : "s"}`);
      setConfirm(false);
      runPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setEditDates((item.entry_days || []).map((d) => d.date || ""));
  };

  const saveEdit = async () => {
    if (editDates.some((d) => !d)) { toast.error("Set a date for every day"); return; }
    setSavingEdit(true);
    try {
      await api.post(`/admin/timesheet-date-fix/manual/${editing.id}`, { entry_dates: editDates });
      toast.success("Dates corrected");
      setEditing(null);
      runPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <AdminLayout nav={amdenNav} title="Timesheet Date Fix">
      <PageHeader title="Timesheet Date Fix" subtitle="Preview and correct timesheets whose day/date was shifted by the old summer-time bug">
        <Button data-testid="datefix-preview-btn" onClick={() => runPreview()} disabled={loading} className="bg-slate-900 text-white">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ListChecks className="h-4 w-4 mr-1" />Run preview</>}
        </Button>
      </PageHeader>

      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Company scope</label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="datefix-scope" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Thoroughness</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="datefix-mode" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="B">All summer weeks (recommended)</SelectItem>
                <SelectItem value="A">Only provably-wrong weeks</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-3 max-w-2xl">
          Preview is read-only. <b>All summer weeks</b> corrects every past summer-period week (safe only for weeks created before this fix went live). <b>Only provably-wrong weeks</b> corrects just the weeks whose stored dates clearly don't line up. Clock-change weeks are listed separately for a human to check and are never auto-corrected.
        </p>
        <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap items-center gap-3">
          <Button data-testid="datefix-fix-all-btn" onClick={() => { setScope("all"); setMode("B"); runPreview("all", "B"); }} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Fix all summer weeks now</>}
          </Button>
          <span className="text-xs text-zinc-500">Loads a full preview across every company — you still confirm before anything is written.</span>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard testId="datefix-total" label="Auto-correctable" value={result.count} icon={CalendarClock} accent />
            <KpiCard testId="datefix-review" label="Needs review" value={result.review_count} icon={AlertTriangle} />
            <KpiCard testId="datefix-companies" label="Companies affected" value={result.companies.length} icon={Building2} />
            <KpiCard testId="datefix-selected" label="Selected to fix" value={selectedIds.length} icon={ListChecks} />
          </div>

          {result.count > 0 && (
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 cursor-pointer">
                <Checkbox data-testid="datefix-select-all" checked={allChecked} onCheckedChange={toggleAll} />
                Select all ({items.length})
              </label>
              <Button data-testid="datefix-apply-btn" disabled={selectedIds.length === 0} onClick={() => setConfirm(true)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                <Wrench className="h-4 w-4 mr-1" />Apply corrections ({selectedIds.length})
              </Button>
            </div>
          )}

          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`datefix-row-${it.id}`} className="bg-white border border-zinc-200 rounded-md overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-zinc-50 border-b border-zinc-100">
                  <Checkbox data-testid={`datefix-check-${it.id}`} checked={!!selected[it.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [it.id]: !!v }))} />
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-sm text-slate-900 truncate">{it.employee_name} <span className="text-zinc-400 font-normal">· {it.company_name}</span></div>
                    <div className="text-xs text-zinc-500 flex items-center gap-1.5 num">
                      Week {fmtDate(it.old_week_start)} <ArrowRight className="h-3 w-3 text-amber-500" /> {fmtDate(it.new_week_start)}
                      <span className="ml-1 text-[10px] uppercase tracking-wider bg-zinc-200 text-zinc-600 rounded px-1.5 py-0.5">{it.status}</span>
                    </div>
                  </div>
                </div>
                {it.entry_changes.length > 0 ? (
                  <div className="divide-y divide-zinc-50">
                    {it.entry_changes.map((ch, i) => (
                      <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                        <div className="font-medium text-zinc-700">{ch.weekday} <span className="text-zinc-400 font-normal">{ch.site_name || ""}</span></div>
                        <div className="flex items-center gap-2 num text-zinc-600">
                          <span className="line-through text-rose-400">{fmtDate(ch.old_date)}</span>
                          <ArrowRight className="h-3.5 w-3.5 text-amber-500" />
                          <span className="font-semibold text-emerald-700">{fmtDate(ch.new_date)}</span>
                          {ch.hours != null && <span className="ml-2 text-zinc-400">{ch.hours}h</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-2 text-xs text-zinc-400">No entries — only the week date is corrected.</div>
                )}
              </div>
            ))}
          </div>

          {reviewItems.length > 0 && (
            <div className="mt-8" data-testid="datefix-review-section">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h2 className="font-heading font-bold text-slate-900">Needs manual review ({reviewItems.length})</h2>
              </div>
              <p className="text-xs text-zinc-500 mb-3 max-w-2xl">These fall on a clock-change week (or the dates don't fit one Mon–Sun week), so they aren't auto-corrected. Open each week in the app and check the days line up; re-saving it will store the correct dates.</p>
              <div className="space-y-3">
                {reviewItems.map((it) => (
                  <div key={it.id} data-testid={`datefix-review-${it.id}`} className="bg-white border border-amber-200 rounded-md overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border-b border-amber-100">
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-sm text-slate-900 truncate">{it.employee_name} <span className="text-zinc-400 font-normal">· {it.company_name}</span></div>
                        <div className="text-xs text-zinc-500 num">Week {fmtDate(it.old_week_start)} · <span className="text-amber-700 font-medium">{it.reason}</span></div>
                      </div>
                      <Button data-testid={`datefix-editdates-${it.id}`} size="sm" variant="outline" className="h-8 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit dates</Button>
                    </div>
                    {it.entry_days?.length > 0 && (
                      <div className="divide-y divide-zinc-50">
                        {it.entry_days.map((d, i) => (
                          <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                            <div className="font-medium text-zinc-700">{d.weekday} <span className="text-zinc-400 font-normal">{d.site_name || ""}</span></div>
                            <div className="num text-zinc-600">{fmtDate(d.date)}{d.hours != null && <span className="ml-2 text-zinc-400">{d.hours}h</span>}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Correct dates — {editing?.employee_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">{editing?.company_name} · week starting {fmtDate(editing?.old_week_start)}. Set each day to its true calendar date — the week is recalculated from the earliest day. Hours are untouched.</p>
          <div className="space-y-2 mt-2 max-h-[50vh] overflow-y-auto">
            {(editing?.entry_days || []).map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-medium text-zinc-700 truncate">{d.site_name || "Site"}{d.hours != null && <span className="text-zinc-400 font-normal"> · {d.hours}h</span>}</div>
                  <div className="text-xs text-zinc-400">currently {fmtDate(d.date)} ({d.weekday})</div>
                </div>
                <Input data-testid={`manual-date-${i}`} type="date" value={editDates[i] || ""} onChange={(e) => setEditDates((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} className="h-10 w-44" />
              </div>
            ))}
            {(editing?.entry_days || []).length === 0 && <div className="text-sm text-zinc-400">This week has no day entries to edit.</div>}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="manual-save-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={saveEdit} disabled={savingEdit || (editing?.entry_days || []).length === 0}>
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save dates"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Apply date corrections?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">
            This will move <strong>{selectedIds.length}</strong> timesheet{selectedIds.length === 1 ? "" : "s"} to the correct calendar dates. Hours stay on the same weekday. This updates the data directly and cannot be auto-undone.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirm(false)}>Cancel</Button>
            <Button data-testid="datefix-confirm-apply" className="bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={apply} disabled={applying}>
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
